import { useCallback, useMemo, useState } from 'react';
import { BedDouble } from 'lucide-react';

type RemotePhoto = { readonly id: string; readonly url: string };

/**
 * As fotos que AINDA podem aparecer: a URL que disparou `error` sai do conjunto
 * (#4359). Galeria com contador e setas navega só por elas — "1/8" com oito fotos
 * que nunca renderizam é pior que "1/1", e o ícone de imagem quebrada do browser
 * nunca deve chegar à tela.
 *
 * Guarda a URL, não o id: o id da view é posicional (`<room>-photo-1`) e um bloco
 * novo reusa os mesmos ids com outras URLs — a lição do `HotelOfferPhoto`.
 */
export function useLivePhotos<Photo extends RemotePhoto>(
  photos: readonly Photo[],
): { readonly live: readonly Photo[]; readonly fail: (url: string) => void } {
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const live = useMemo(
    () => (failed.size === 0 ? photos : photos.filter((photo) => !failed.has(photo.url))),
    [photos, failed],
  );
  const fail = useCallback((url: string) => {
    setFailed((current) => (current.has(url) ? current : new Set(current).add(url)));
  }, []);
  return { live, fail };
}

/**
 * A posição na lista de fotos vivas, presa à FOTO e não ao número: uma foto
 * anterior que cai no meio da navegação não pode trocar o quadro debaixo de quem
 * está olhando. Se a própria foto em tela cai, fica a que ocupou o lugar dela.
 */
export function useLivePhotoIndex(
  live: readonly RemotePhoto[],
): readonly [number, (index: number) => void] {
  const [selected, setSelected] = useState<{ readonly id?: string; readonly index: number }>({
    index: 0,
  });
  const found = selected.id === undefined ? -1 : live.findIndex(({ id }) => id === selected.id);
  const index = found >= 0 ? found : Math.min(selected.index, Math.max(live.length - 1, 0));
  // A substituta passa a ser a foto selecionada: sem isto, a queda seguinte de
  // uma foto anterior a trocaria pela próxima, de novo pelo número.
  const replacement = live[index]?.id;
  if (selected.id !== undefined && found < 0 && replacement !== undefined) {
    setSelected({ id: replacement, index });
  }
  const select = useCallback(
    (next: number) => {
      setSelected({ id: live[next]?.id, index: next });
    },
    [live],
  );
  return [index, select] as const;
}

/** O lugar da foto quando não há foto viva: o ícone sobre `bg-muted` dos cards de
 *  quarto, nunca a imagem quebrada do browser. Decorativo — o nome do quarto ou
 *  do hotel já está ao lado. */
export function HotelPhotoPlaceholder({ className = '' }: { readonly className?: string }) {
  return (
    <span
      data-slot="hotel-photo-placeholder"
      className={`bg-muted text-muted-foreground grid place-items-center ${className}`}
    >
      <BedDouble className="size-6" aria-hidden />
    </span>
  );
}
